import React from 'react';
/**
 * `PriceDisplay` renders the selected product's price in the appropriate format
 * for the chosen payment method.
 *
 * Behaviour:
 * - Uses `getVariantInstalmentTotalPrice` to derive first/subsequent instalment
 *   amounts (including linked products when applicable).
 * - Renders different layouts for `Single`, `Subscription`, and instalment
 *   payment methods.
 * - Supports hiding the "/per month" label for subscription pricing.
 * - Formats numbers using `formatNumber` and the supplied `culture`.
 */

import {
  getSelectedVariant,
  getVariantInstalmentTotalPrice
} from './ProductMethods';
import { ProductType, SelectedLinkedProduct } from './CommonTypes';
import { PaymentMethod } from './Constants';
import { formatNumber } from './Formatter';
import { useTranslation } from 'react-i18next';
import { useCurrencySymbol } from '../../hooks/useCurrencySymbol';

type PriceDisplayProps = {
  product: ProductType;
  noOfInstallments: number;
  className: string;
  paymentMethod?: string;
  hideInstallmentLabel?: boolean;
  selectedLinkedProducts: SelectedLinkedProduct[];
  culture: string;
};

const PriceDisplay = ({
  product,
  noOfInstallments,
  className,
  hideInstallmentLabel,
  selectedLinkedProducts,
  culture
}: PriceDisplayProps): JSX.Element => {
  const selectedVariant = getSelectedVariant(product.productVariants);
  const currencySymbol = useCurrencySymbol();
  const installmentPrice = getVariantInstalmentTotalPrice(
    product.productId,
    selectedVariant,
    noOfInstallments,
    selectedLinkedProducts,
    product.isVariablePrice
  );
  const { t } = useTranslation();

  const perMonthText = t('customer-product-page.per-month-text');
  const totalInstallmentText = t(
    'customer-product-page.total-installments-text',
    {
      totalNumberOfInstallments:
        installmentPrice.totalNumberOfInstallments.toString()
    }
  );

  if (
    product.selectedPaymentMethod &&
    product.selectedPaymentMethod === PaymentMethod.Single
  ) {
    return (
      <div>
        <p className={`${className} bold`}>
          {currencySymbol}
          {formatNumber(installmentPrice.firstInstallmentPrice, 2, culture)}
        </p>
      </div>
    );
  }

  if (
    product.selectedPaymentMethod &&
    product.selectedPaymentMethod === PaymentMethod.Subscription
  ) {
    return (
      <div>
        <p className={`${className} small bold`}>
          {hideInstallmentLabel !== true
            ? `${currencySymbol}${formatNumber(
                installmentPrice.firstInstallmentPrice,
                2,
                culture
              )}/${perMonthText}`
            : `${currencySymbol}${formatNumber(
                installmentPrice.firstInstallmentPrice,
                2,
                culture
              )}`}
        </p>
        {hideInstallmentLabel !== true && (
          <p className='label'>
            {t('customer-product-page.ongoing-until-terminated')}
          </p>
        )}
      </div>
    );
  }
  if (installmentPrice.totalNumberOfInstallments <= 1) {
    return (
      <div>
        <p className={`${className} bold`}>
          {currencySymbol}
          {formatNumber(installmentPrice.firstInstallmentPrice, 2, culture)}/
          {perMonthText}
        </p>
      </div>
    );
  }

  if (
    installmentPrice.firstInstallmentPrice ===
    installmentPrice.subsequentInstallmentPrice
  ) {
    return (
      <div>
        <p className={`${className} bold`}>
          {currencySymbol}
          {formatNumber(installmentPrice.firstInstallmentPrice, 2, culture)}/
          {perMonthText}
        </p>
        <p className='label'>{totalInstallmentText}</p>
        <p className='label'>
          {t('customer-product-page.total-amount-text', {
            currencySymbol: currencySymbol,
            priceTotal: formatNumber(
              installmentPrice.totalMonthlyAmount,
              2,
              culture
            )
          })}
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className={`${className} bold`}>
        {currencySymbol}
        {formatNumber(installmentPrice.subsequentInstallmentPrice, 2, culture)}/
        {perMonthText}
      </p>
      <p className='label'>{totalInstallmentText}</p>
      <p className='label'>{`(${t('customer-product-page.first-payment-text')} ${currencySymbol}${formatNumber(
        installmentPrice.firstInstallmentPrice,
        2,
        culture
      )})`}</p>
      <p className='label'>
        {t('customer-product-page.total-amount-text', {
          currencySymbol: currencySymbol,
          priceTotal: formatNumber(
            installmentPrice.totalMonthlyAmount,
            2,
            culture
          )
        })}
      </p>
    </div>
  );
};

export default PriceDisplay;
